import { useContext } from "react";

import { UserContext } from "App/contexts";
import { isRegalVoiceUser } from "Services/AuthService";
import { useFlags } from "Services/FeatureFlagService";

/**
 * Hook that determines which top-level pages should be available to the user
 * based on feature flags and user roles.
 */
export function usePageAvailability() {
    const { isAdmin } = useContext(UserContext);
    const {
        segments,
        campaigns,
        tasks,
        journeys,
        ivrPage,
        adminSettings,
        dashboards,
        audiencePage,
        agentsPage,
        queuesPage,
        recentActivity,
        regalLivePage,
        transcriptionsPage,
        hasAiAgentsDemo,
        showRoutingRules,
        enableTrackerAiAssist,
        enablesTheAiScorecardsEdit,
        conversationIntelligenceCustomAnalysis,
        conversationsIntelligenceInsights,
        enableAiAgentCloneEdit,
        rolloutUiKnowledgeBase,
        enableAiAgentVariants,
        observabilityPageBehavior,
        showUserManagement,
        email: emailVersion,
        hasPreRecordedVmDrops,
        displayAgentDesktopPermissionsPage,
        showBusinessProfiles,
        showBrandedNumbers,
        showCustomUserAttributes,
        hasRegalCopilotNav,
        showTrustOverview,
        mockRegalLiveAlternative,
        showAgentChatTranscripts,
        hasCustomFunctionsPage,
    } = useFlags();

    const isAgent = !isAdmin;

    const showADToAgent = !isAdmin;
    const showADToAdmin = isAdmin;

    // Create consistently named flags for features
    const showRecordings = transcriptionsPage;
    const showObservability = observabilityPageBehavior?.enabled ?? false;
    const showMoments = enableTrackerAiAssist && showRecordings;
    const showCustomAnalysis = conversationIntelligenceCustomAnalysis;
    const showScorecards = enablesTheAiScorecardsEdit;
    const showInsights = conversationsIntelligenceInsights;

    const showTasks = tasks;
    const showAgents = agentsPage;
    const showQueues = queuesPage;
    const showRecentActivity = recentActivity;

    const showContacts = audiencePage;
    const showSegments = segments && adminSettings;

    // Orchestration features - matching OrchestrationSubNav UI labels
    const showJourneys = journeys;
    const showCampaigns = campaigns && adminSettings;
    const showIVR = ivrPage;
    const showTaskRouting = showRoutingRules && adminSettings;
    const showCustomFunctions = hasCustomFunctionsPage;

    // AI Agents features - matching AI Agents pages
    const showCustomAgents = enableAiAgentCloneEdit;
    const showKnowledgeBase = rolloutUiKnowledgeBase;
    const showAgentVariants = enableAiAgentVariants;

    const showPreRecordedVmDrops = hasPreRecordedVmDrops;
    const showAgentPermissions = displayAgentDesktopPermissionsPage;

    const showLiveOps = showAgents || showTasks || showQueues || showRecentActivity;

    const showOrchestration = showCampaigns || showJourneys || showTaskRouting || showIVR || showCustomFunctions;

    const showConvoIntel =
        showRecordings ||
        showObservability ||
        showMoments ||
        showCustomAnalysis ||
        showScorecards ||
        showInsights ||
        showAgentChatTranscripts;

    const showAIAgents = hasAiAgentsDemo || showCustomAgents || showKnowledgeBase;

    const showAudience = showContacts || showSegments;

    const showRegalLive = regalLivePage;

    const showReporting = dashboards;

    const showEmail = emailVersion > 0;
    const showAgentSettings = showEmail || hasPreRecordedVmDrops;
    const showSip = adminSettings && isRegalVoiceUser();

    const showRegalCopilot = hasRegalCopilotNav;
    // Compliance settings are always available, so admin settings are always reachable for admins.
    const showSettings = adminSettings && isAdmin;

    return {
        // User role helpers
        isAdmin,
        isAgent,
        // Header page Navigation
        showAgentSettings,
        // Top-level page visibility
        showRegalLive,
        mockRegalLiveAlternative,
        showReporting,
        showADToAgent,
        showADToAdmin,
        showRegalCopilot,
        // Top-level page Sub Nav
        showLiveOps,
        showOrchestration,
        showConvoIntel,
        showAIAgents,
        showAudience,
        showSettings,
        // Individual feature flags for sub-navigation
        liveOpsPages: {
            showTasks,
            showAgents,
            showQueues,
            showRecentActivity,
        },
        convoIntelPages: {
            showRecordings,
            showObservability,
            observabilityPageBehavior,
            showMoments,
            showCustomAnalysis,
            showScorecards,
            showInsights,
            showAgentChats: showAgentChatTranscripts,
        },
        audiencePages: {
            showContacts,
            showSegments,
        },
        orchestrationPages: {
            showJourneys,
            showCampaigns,
            showIVR,
            showTaskRouting,
            showCustomFunctions,
        },
        aiAgentsPages: {
            showCustomAgents,
            showKnowledgeBase,
            showAgentVariants,
        },
        agentSettingsPages: {
            showEmail,
            showPreRecordedVmDrops,
        },
        settingsPages: {
            showRoutingRules,
            showUserManagement,
            showEmail,
            showPreRecordedVmDrops,
            showAgentPermissions,
            showAgentSettings,
            showBusinessProfiles,
            showBrandedNumbers,
            showSip,
            showCustomUserAttributes,
            showTrustOverview,
        },
    };
}
